// =====================================================================
// PropertyCuts — Supabase service layer
// =====================================================================
// Re-exports the shared client and provides PropertyCuts-specific CRUD,
// storage, and credit helpers. Mirrors apps/videomotion/services/supabase.ts.
// =====================================================================

import { supabase, isConfigured } from '@rehub/supabase';
import {
  checkAdminStatus as checkAdminStatusShared,
  getAdminRole as getAdminRoleShared,
  type AdminRole,
} from '@rehub/admin';
import {
  fetchBrandProfiles as fetchBrandProfilesShared,
  createBrandProfile as createBrandProfileShared,
  updateBrandProfile as updateBrandProfileShared,
  deleteBrandProfile as deleteBrandProfileShared,
  type MediaBrandProfile,
  type MediaBrandProfileInput,
} from '@rehub/branded-viewer';
import {
  applyBrandPlates,
  emptyEditorState,
  parsePlateStyle,
  type EditorState,
  type PlateStyle,
} from '@rehub/propertycut-timeline';
import type {
  PropertyCutProject,
  PropertyCutAsset,
  PropertyCutRender,
  PropertyCutMusicTrack,
  TemplateId,
  ListingData,
  AgentData,
  MotionSource,
  MotionProjectSummary,
  HyperDuskImageRow,
  ImageMotionClipRow,
  AssetKind,
  TransitionStyle,
  RenderFormat,
  ProjectStatus,
  StartWorkflow,
} from '../types';
import { STORAGE_BUCKET } from '../constants';
import { logoStoragePath } from './logoPath';

const IMAGEMOTION_BUCKET = 'imagemotion-uploads';

export { supabase, isConfigured };

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

// =====================================================================
// Auth helpers (SSO via hash-param token handoff)
// =====================================================================

export interface SsoTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Reads `#sso_access_token=...&sso_refresh_token=...` from the URL hash if
 * present (set by other ReHub apps when linking here). Strips them from the
 * URL after reading. Pass to supabase.auth.setSession() to adopt the session.
 *
 * Mirrors apps/videomotion/App.tsx:65–89 — keep them in sync.
 */
export function consumeSsoTokensFromHash(): SsoTokens | null {
  if (typeof window === 'undefined') return null;
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const accessToken = hashParams.get('sso_access_token');
  const refreshToken = hashParams.get('sso_refresh_token');
  if (!accessToken || !refreshToken) return null;
  window.history.replaceState({}, document.title, window.location.pathname);
  return { accessToken, refreshToken };
}

// =====================================================================
// Credits
// =====================================================================

export async function fetchUserCredits(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('profiles')
    .select('credits')
    .eq('id', userId)
    .single();

  if (error || !data) return 0;
  return data.credits ?? 0;
}

// =====================================================================
// Projects
// =====================================================================

/**
 * Asset shape embedded alongside a project for thumbnail derivation.
 * Only the columns we need to pick + re-sign a still image.
 */
interface ThumbnailAssetRow {
  storage_path: string | null;
  signed_url: string | null;
  order_index: number;
  kind: AssetKind;
}

/**
 * Pick the best asset to use as a project thumbnail. Prefers a still
 * image (regular photo, then floor plan, earliest by order_index) which
 * renders cleanly inside an `<img>`. When a project has no stills (e.g.
 * it's built entirely from imported video clips) it falls back to the
 * first video, which the dashboard renders via a `<video>` element so its
 * first frame becomes the thumbnail.
 */
function pickThumbnailAsset(assets: ThumbnailAssetRow[]): ThumbnailAssetRow | null {
  const byOrder = [...assets].sort((a, b) => a.order_index - b.order_index);
  return (
    byOrder.find((a) => a.kind === 'photo') ??
    byOrder.find((a) => a.kind === 'floor_plan') ??
    byOrder.find((a) => a.kind === 'video') ??
    null
  );
}

export async function fetchProjects(): Promise<PropertyCutProject[]> {
  const { data, error } = await supabase
    .from('propertycut_projects')
    .select('*, propertycut_assets(storage_path, signed_url, order_index, kind)')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Fetch propertycut projects error:', error);
    return [];
  }

  const rows = (data ?? []) as (PropertyCutProject & {
    propertycut_assets?: ThumbnailAssetRow[];
  })[];

  // Derive a fresh thumbnail from each project's first photo asset.
  // Signed URLs expire, so we re-sign on every load rather than persist
  // a stale URL on the project row (mirrors imagemotion's dashboard).
  return Promise.all(
    rows.map(async (row) => {
      const assets = Array.isArray(row.propertycut_assets) ? row.propertycut_assets : [];
      delete row.propertycut_assets;

      // Honour an explicit thumbnail on the project if one is ever set
      // (e.g. by the render worker); otherwise fall back to a photo, or a
      // video frame when the project has no stills.
      if (!row.thumbnail_url) {
        const pick = pickThumbnailAsset(assets);
        if (pick) {
          row.thumbnail_url = await getFreshSignedUrl(pick.storage_path, pick.signed_url);
          row.thumbnail_is_video = pick.kind === 'video';
        }
      }
      return row as PropertyCutProject;
    }),
  );
}

export async function fetchProject(projectId: string): Promise<PropertyCutProject | null> {
  const { data, error } = await supabase
    .from('propertycut_projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error || !data) return null;
  return data as PropertyCutProject;
}

export async function createProject(params: {
  userId: string;
  templateId: TemplateId;
  title?: string | null;
  listingData?: ListingData;
  agentData?: AgentData;
  outputFormat?: RenderFormat | null;
  editorState?: EditorState | null;
  startWorkflow?: StartWorkflow | null;
}): Promise<string> {
  const row = {
    user_id: params.userId,
    template_id: params.templateId,
    title: params.title ?? null,
    listing_data: params.listingData ?? {},
    agent_data: params.agentData ?? {},
    output_format: params.outputFormat ?? null,
    editor_state: params.editorState ?? null,
    status: 'draft',
  };
  const insert = (values: Record<string, unknown>) =>
    supabase.from('propertycut_projects').insert(values).select('id').single();

  let { data, error } = await insert(
    params.startWorkflow ? { ...row, start_workflow: params.startWorkflow } : row,
  );
  // The workflow is only a starting preset; databases without the column still create the project.
  if (error && params.startWorkflow && /start_workflow/.test(error.message ?? '')) {
    ({ data, error } = await insert(row));
  }
  if (error || !data) throw error ?? new Error('Could not create the project');
  return data.id;
}

export async function saveEditorDocument(params: {
  projectId: string;
  editorState: EditorState;
  outputFormat: RenderFormat;
  title: string;
  listing: ListingData;
  agent: AgentData;
  status: ProjectStatus;
}): Promise<void> {
  const nextStatus = params.status === 'draft' ? 'editing' : params.status;
  const { error } = await supabase
    .from('propertycut_projects')
    .update({
      editor_state: params.editorState,
      output_format: params.outputFormat,
      title: params.title || null,
      listing_data: params.listing,
      agent_data: params.agent,
      status: nextStatus,
    })
    .eq('id', params.projectId);
  if (error) throw error;
}

export async function updateProjectTitle(projectId: string, title: string): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .update({ title: title || null })
    .eq('id', projectId);
  if (error) throw error;
}

/**
 * Merge-write `listing_data` (jsonb). Pass undefined for any field that
 * should be removed from the JSON object — null values are preserved.
 */
export async function updateProjectListingData(
  projectId: string,
  listing: ListingData,
): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .update({ listing_data: listing })
    .eq('id', projectId);
  if (error) throw error;
}

export async function updateProjectTransition(
  projectId: string,
  transitionStyle: TransitionStyle,
): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .update({ transition_style: transitionStyle })
    .eq('id', projectId);
  if (error) throw error;
}

export async function updateProjectAgentData(
  projectId: string,
  agent: AgentData,
): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .update({ agent_data: agent })
    .eq('id', projectId);
  if (error) throw error;
}

/**
 * Upload a branding logo into the propertycut bucket and return a signed
 * URL. Stored under `logos/{userId}/...` so it's namespaced away from the
 * project assets. The worker re-signs this URL at render time.
 */
export async function uploadAgentLogo(
  userId: string,
  file: File,
): Promise<string> {
  const ext = uniqueExt(file.name, 'png');
  const storagePath = `logos/${userId}/${randomId()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });
  if (uploadErr) throw uploadErr;

  const { data: signed, error: signErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
  if (signErr) throw signErr;

  return signed.signedUrl;
}

export async function deleteProject(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .delete()
    .eq('id', projectId);
  if (error) throw error;
}

// =====================================================================
// Assets
// =====================================================================

export async function fetchAssets(projectId: string): Promise<PropertyCutAsset[]> {
  const { data, error } = await supabase
    .from('propertycut_assets')
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('Fetch propertycut assets error:', error);
    return [];
  }
  return (data ?? []) as PropertyCutAsset[];
}

/**
 * Persist a new ordering for a project's assets. `orderedIds` is the full
 * list of asset ids in the desired order; each row's `order_index` is set
 * to its position. The list is small (a handful of clips) so sequential
 * updates are fine. Throws on the first failure so callers can revert
 * optimistic UI state.
 */
export async function reorderAssets(
  projectId: string,
  orderedIds: string[],
): Promise<void> {
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase
      .from('propertycut_assets')
      .update({ order_index: i })
      .eq('id', orderedIds[i])
      .eq('project_id', projectId);
    if (error) throw error;
  }
}

/**
 * Update a single asset's clip-editing fields. Pass `null` for
 * `clipDurationSeconds` to fall back to the template default beat.
 */
export async function updateAssetClip(
  assetId: string,
  clip: { trimStartSeconds: number; clipDurationSeconds: number | null },
): Promise<PropertyCutAsset> {
  const { data, error } = await supabase
    .from('propertycut_assets')
    .update({
      trim_start_seconds: clip.trimStartSeconds,
      clip_duration_seconds: clip.clipDurationSeconds,
    })
    .eq('id', assetId)
    .select('*')
    .single();
  if (error) throw error;
  return data as PropertyCutAsset;
}

// =====================================================================
// Renders
// =====================================================================

export async function fetchRenders(projectId: string): Promise<PropertyCutRender[]> {
  const { data, error } = await supabase
    .from('propertycut_renders')
    // Not editor_state_snapshot: it is large and this list is polled.
    .select(
      'id, project_id, format, status, shotstack_render_id, output_url, output_path, output_duration_seconds, output_file_size_bytes, progress, credits_charged, error_message, queued_at, started_at, completed_at, created_at, base_credits',
    )
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Fetch propertycut renders error:', error);
    return [];
  }
  const rows = (data ?? []) as PropertyCutRender[];
  return Promise.all(rows.map(async (row) => {
    if (row.status !== 'completed' || !row.output_path) return row;
    const url = await renderOutputUrl(row.output_path, row.completed_at, row.output_url);
    return url ? { ...row, output_url: url } : row;
  }));
}

const RENDER_URL_SECONDS = 60 * 60 * 24 * 7;
/** Re-sign once less than this is left, well before any open player needs it. */
const RENDER_URL_RENEW_MS = 60 * 60 * 24 * 1000;
const renderUrlCache = new Map<string, { url: string; expiresAt: number }>();

/**
 * Signed URL for a finished export, reused while it is valid. The render list
 * is polled, and a new URL on every poll makes the <video> reload and flicker.
 * Keyed by completion time too, because a re-render overwrites the same path.
 */
async function renderOutputUrl(
  storagePath: string,
  completedAt: string | null,
  fallbackUrl: string | null,
): Promise<string | null> {
  const key = `${storagePath}@${completedAt ?? ''}`;
  const cached = renderUrlCache.get(key);
  if (cached && cached.expiresAt - Date.now() > RENDER_URL_RENEW_MS) return cached.url;
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, RENDER_URL_SECONDS);
  if (error || !data?.signedUrl) return cached?.url ?? fallbackUrl;
  renderUrlCache.set(key, { url: data.signedUrl, expiresAt: Date.now() + RENDER_URL_SECONDS * 1000 });
  return data.signedUrl;
}

/**
 * Saves a finished export to the user's downloads. A plain link to the signed
 * URL opens the video in a new tab because the file is on another origin, so
 * the browser ignores the download attribute. Pull the bytes and hand the
 * browser a same-origin file instead.
 */
export async function downloadRenderFile(args: {
  outputPath: string | null;
  outputUrl: string;
  filename: string;
}): Promise<void> {
  let blob: Blob | null = null;
  if (args.outputPath) {
    const { data, error } = await supabase.storage.from(STORAGE_BUCKET).download(args.outputPath);
    if (!error && data) blob = data;
  }
  if (!blob) {
    const res = await fetch(args.outputUrl);
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    blob = await res.blob();
  }

  const file = new Blob([blob], { type: 'application/octet-stream' });
  const blobUrl = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = args.filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel a large file before the browser reads it.
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

/**
 * Live subscription to the renders table for a single project. The
 * callback fires on insert, update, or delete and receives the full
 * (refreshed) list so callers can replace state without bookkeeping.
 *
 * Returns an unsubscribe function; call it on unmount.
 */
export function subscribeToProjectRenders(
  projectId: string,
  onChange: (renders: PropertyCutRender[]) => void,
): () => void {
  const channel = supabase
    .channel(`propertycut_renders:${projectId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'propertycut_renders',
        filter: `project_id=eq.${projectId}`,
      },
      async () => {
        const next = await fetchRenders(projectId);
        onChange(next);
      },
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// =====================================================================
// Music (curated library)
// =====================================================================

export async function fetchMusicLibrary(): Promise<PropertyCutMusicTrack[]> {
  const { data, error } = await supabase
    .from('propertycut_music')
    .select('*')
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('title', { ascending: true });

  if (error) {
    console.error('Fetch propertycut music error:', error);
    return [];
  }
  return (data ?? []) as PropertyCutMusicTrack[];
}

// =====================================================================
// Storage helpers (signed URLs)
// =====================================================================

/**
 * Generate a fresh signed URL from a storage path. Falls back to the
 * provided URL if the path is missing. Same shape as VideoMotion.
 */
export async function getFreshSignedUrl(
  storagePath: string | null,
  fallbackUrl: string | null
): Promise<string | null> {
  if (storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  return fallbackUrl;
}

// =====================================================================
// Admin
// =====================================================================

export async function checkAdminStatus(userId: string): Promise<boolean> {
  return checkAdminStatusShared(supabase, userId);
}

export async function getAdminRole(userId: string): Promise<AdminRole | null> {
  return getAdminRoleShared(supabase, userId);
}

export interface AdminPropertyCutAsset {
  id: string;
  kind: string;
  storage_path: string;
  original_filename: string | null;
  order_index: number;
  duration_seconds: number | null;
}

export interface AdminPropertyCutRender {
  id: string;
  format: string;
  status: string;
  output_path: string | null;
  output_url: string | null;
  output_duration_seconds: number | null;
  output_file_size_bytes: number | null;
  credits_charged: number;
  error_message: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface AdminPropertyCutProject {
  id: string;
  user_id: string;
  title: string;
  status: ProjectStatus;
  output_format: string | null;
  template_id: string;
  listing_address: string | null;
  listing_price: string | null;
  agent_name: string | null;
  agent_email: string | null;
  agent_phone: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  owner_email: string | null;
  owner_name: string | null;
  thumbnail_url: string | null;
  thumbnail_is_video: boolean;
  photo_count: number;
  video_count: number;
  credits_charged: number;
  assets: AdminPropertyCutAsset[];
  renders: AdminPropertyCutRender[];
}

const ADMIN_PROJECT_LIMIT = 500;
const ADMIN_IN_CHUNK = 100;

async function selectInChunks<T>(
  ids: string[],
  query: (chunk: string[]) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += ADMIN_IN_CHUNK) chunks.push(ids.slice(i, i + ADMIN_IN_CHUNK));
  const results = await Promise.all(chunks.map((chunk) => query(chunk)));
  const rows: T[] = [];
  for (const result of results) {
    if (result.error) console.error('Admin chunked select error:', result.error.message);
    if (result.data) rows.push(...result.data);
  }
  return rows;
}

function textField(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Every PropertyCuts project (newest first) with its owner, media and exports.
 * Relies on the admin read policies. Non-admins only receive their own rows.
 */
export async function fetchAdminProjects(): Promise<AdminPropertyCutProject[]> {
  const { data, error } = await supabase
    .from('propertycut_projects')
    .select('id, user_id, title, status, output_format, template_id, listing_data, agent_data, error_message, created_at, updated_at')
    .order('created_at', { ascending: false })
    .limit(ADMIN_PROJECT_LIMIT);

  if (error) throw new Error(error.message);
  const projects = (data ?? []) as Array<Record<string, unknown>>;
  if (projects.length === 0) return [];

  const projectIds = projects.map((project) => String(project.id));
  const userIds = [...new Set(projects.map((project) => String(project.user_id)).filter(Boolean))];

  const [profiles, assets, renders, jobs] = await Promise.all([
    selectInChunks<{ id: string; email: string | null; full_name: string | null }>(userIds, (chunk) =>
      supabase.from('profiles').select('id, email, full_name').in('id', chunk),
    ),
    selectInChunks<AdminPropertyCutAsset & { project_id: string }>(projectIds, (chunk) =>
      supabase
        .from('propertycut_assets')
        .select('id, project_id, kind, storage_path, original_filename, order_index, duration_seconds')
        .in('project_id', chunk)
        .order('order_index', { ascending: true }),
    ),
    selectInChunks<AdminPropertyCutRender & { project_id: string }>(projectIds, (chunk) =>
      supabase
        .from('propertycut_renders')
        .select('id, project_id, format, status, output_path, output_url, output_duration_seconds, output_file_size_bytes, credits_charged, error_message, completed_at, created_at')
        .in('project_id', chunk)
        .order('created_at', { ascending: false }),
    ),
    selectInChunks<{ project_id: string; credit_cost: number; charged: boolean; refunded: boolean }>(projectIds, (chunk) =>
      supabase
        .from('propertycut_jobs')
        .select('project_id, credit_cost, charged, refunded')
        .in('project_id', chunk),
    ),
  ]);

  const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
  const assetsByProject = new Map<string, AdminPropertyCutAsset[]>();
  for (const asset of assets) {
    const list = assetsByProject.get(asset.project_id) ?? [];
    list.push({
      id: asset.id,
      kind: asset.kind,
      storage_path: asset.storage_path,
      original_filename: asset.original_filename,
      order_index: asset.order_index,
      duration_seconds: asset.duration_seconds,
    });
    assetsByProject.set(asset.project_id, list);
  }
  const rendersByProject = new Map<string, AdminPropertyCutRender[]>();
  for (const render of renders) {
    const list = rendersByProject.get(render.project_id) ?? [];
    list.push({
      id: render.id,
      format: render.format,
      status: render.status,
      output_path: render.output_path,
      output_url: render.output_url,
      output_duration_seconds: render.output_duration_seconds,
      output_file_size_bytes: render.output_file_size_bytes,
      credits_charged: Number(render.credits_charged) || 0,
      error_message: render.error_message,
      completed_at: render.completed_at,
      created_at: render.created_at,
    });
    rendersByProject.set(render.project_id, list);
  }
  const jobCredits = new Map<string, number>();
  for (const job of jobs) {
    if (!job.charged || job.refunded) continue;
    jobCredits.set(job.project_id, (jobCredits.get(job.project_id) ?? 0) + (Number(job.credit_cost) || 0));
  }

  const built = projects.map((project) => {
    const id = String(project.id);
    const userId = String(project.user_id);
    const owner = profileById.get(userId);
    const listing = (project.listing_data ?? {}) as Record<string, unknown>;
    const agent = (project.agent_data ?? {}) as Record<string, unknown>;
    const projectAssets = (assetsByProject.get(id) ?? []).sort((a, b) => a.order_index - b.order_index);
    const thumb = projectAssets.find((asset) => asset.kind === 'photo')
      ?? projectAssets.find((asset) => asset.kind === 'floor_plan')
      ?? projectAssets.find((asset) => asset.kind === 'video')
      ?? null;
    const projectRenders = rendersByProject.get(id) ?? [];
    const renderCredits = projectRenders.reduce((sum, render) => sum + render.credits_charged, 0);
    return {
      id,
      user_id: userId,
      title: textField(project.title) ?? 'Untitled',
      status: project.status as ProjectStatus,
      output_format: textField(project.output_format),
      template_id: String(project.template_id ?? ''),
      listing_address: textField(listing.address),
      listing_price: textField(listing.price),
      agent_name: textField(agent.name),
      agent_email: textField(agent.email),
      agent_phone: textField(agent.phone),
      error_message: textField(project.error_message),
      created_at: String(project.created_at ?? ''),
      updated_at: String(project.updated_at ?? project.created_at ?? ''),
      owner_email: owner?.email ?? null,
      owner_name: owner?.full_name ?? null,
      thumbnail_url: null as string | null,
      thumbnail_is_video: thumb?.kind === 'video',
      thumbnail_path: thumb?.storage_path ?? null,
      photo_count: projectAssets.filter((asset) => asset.kind === 'photo' || asset.kind === 'floor_plan').length,
      video_count: projectAssets.filter((asset) => asset.kind === 'video').length,
      credits_charged: renderCredits + (jobCredits.get(id) ?? 0),
      assets: projectAssets,
      renders: projectRenders,
    };
  });

  const thumbPaths = [...new Set(built.map((project) => project.thumbnail_path).filter((path): path is string => !!path))];
  const thumbByPath = new Map<string, string>();
  for (let i = 0; i < thumbPaths.length; i += ADMIN_IN_CHUNK) {
    const { data: signed } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrls(thumbPaths.slice(i, i + ADMIN_IN_CHUNK), 60 * 60);
    for (const item of signed ?? []) {
      if (item.path && item.signedUrl) thumbByPath.set(item.path, item.signedUrl);
    }
  }

  return built.map(({ thumbnail_path, ...project }) => ({
    ...project,
    thumbnail_url: thumbnail_path ? thumbByPath.get(thumbnail_path) ?? null : null,
  }));
}

// =====================================================================
// Saved logo opening and end (one per person)
// =====================================================================

export interface SavedBrandKit {
  logoUrl: string | null;
  opening: PlateStyle | null;
  ending: PlateStyle | null;
  updatedAt: string;
}

async function freshLogoUrl(
  userId: string,
  path: string | null,
  fallback: string | null,
): Promise<string | null> {
  const stored = path && path.startsWith(`logos/${userId}/`) && !path.split('/').includes('..')
    ? path
    : null;
  if (!stored) return fallback;
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(stored, 60 * 60 * 24 * 7);
  if (error || !data?.signedUrl) return fallback;
  return data.signedUrl;
}

function kitFromRow(
  userId: string,
  row: { logo_url: string | null; logo_path: string | null; opening: unknown; ending: unknown; updated_at: string },
): Promise<SavedBrandKit> {
  return freshLogoUrl(userId, row.logo_path, row.logo_url).then((logoUrl) => ({
    logoUrl,
    opening: parsePlateStyle(row.opening),
    ending: parsePlateStyle(row.ending),
    updatedAt: row.updated_at,
  }));
}

export async function fetchBrandKit(userId: string): Promise<SavedBrandKit | null> {
  const { data, error } = await supabase
    .from('propertycut_brand_kits')
    .select('logo_url, logo_path, opening, ending, updated_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const kit = await kitFromRow(userId, data);
  if (!kit.opening && !kit.ending && !kit.logoUrl) return null;
  return kit;
}

export async function saveBrandKit(
  userId: string,
  kit: { logoUrl?: string | null; opening: PlateStyle | null; ending: PlateStyle | null },
): Promise<SavedBrandKit> {
  const logoUrl = kit.logoUrl ?? null;
  const logoPath = logoStoragePath(logoUrl, userId);
  const { data, error } = await supabase
    .from('propertycut_brand_kits')
    .upsert(
      {
        user_id: userId,
        logo_url: logoUrl,
        logo_path: logoPath,
        opening: kit.opening,
        ending: kit.ending,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    )
    .select('logo_url, logo_path, opening, ending, updated_at')
    .single();
  if (error || !data) throw error ?? new Error('Could not save the opening and end');
  return kitFromRow(userId, data);
}

export async function deleteBrandKit(userId: string): Promise<void> {
  const { error } = await supabase
    .from('propertycut_brand_kits')
    .delete()
    .eq('user_id', userId);
  if (error) throw error;
}

/** Editor document and logo for a new video, including the saved plates when there are any. */
export async function brandingForNewProject(userId: string): Promise<{
  editorState: EditorState;
  agent: AgentData;
}> {
  const blank = { editorState: emptyEditorState(), agent: {} as AgentData };
  try {
    const kit = await fetchBrandKit(userId);
    if (!kit?.opening && !kit?.ending) return blank;
    return {
      editorState: applyBrandPlates(emptyEditorState(), {
        opening: kit?.opening ?? null,
        ending: kit?.ending ?? null,
      }),
      agent: kit?.logoUrl ? { logo_url: kit.logoUrl } : {},
    };
  } catch (err) {
    console.warn('[propertycut] saved branding unavailable:', err);
    return blank;
  }
}

// =====================================================================
// Brand profiles (reusable, user-level — shared across ReHub apps)
// =====================================================================
// Backed by the shared `media_brand_profiles` table, so a user's brands
// created in ImageMotion / VideoMotion appear here too (and vice-versa).
// Mirrors apps/imagemotion/services/supabase.ts.

export type { MediaBrandProfile, MediaBrandProfileInput };

export function fetchBrandProfiles(userId: string): Promise<MediaBrandProfile[]> {
  return fetchBrandProfilesShared(supabase, userId);
}

export function createBrandProfile(
  userId: string,
  input: MediaBrandProfileInput,
): Promise<MediaBrandProfile> {
  return createBrandProfileShared(supabase, userId, input);
}

export function updateBrandProfile(
  userId: string,
  id: string,
  input: MediaBrandProfileInput,
): Promise<void> {
  return updateBrandProfileShared(supabase, userId, id, input);
}

export function deleteBrandProfile(userId: string, id: string): Promise<void> {
  return deleteBrandProfileShared(supabase, userId, id);
}

/**
 * Upload a brand-profile logo into the propertycut bucket and return a
 * long-lived signed URL. Stored under the `logos/` folder (the only
 * branding folder allowed by the bucket's insert policy). A 1-year URL
 * keeps the logo stable on saved profiles; the render worker also
 * re-signs propertycut-uploads logo URLs at render time.
 */
export async function uploadBrandProfileLogo(file: File, userId: string): Promise<string> {
  const ext = uniqueExt(file.name, 'png');
  const storagePath = `logos/${userId}/brand-${randomId()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, {
      cacheControl: '31536000',
      upsert: false,
      contentType: file.type || undefined,
    });
  if (uploadErr) throw uploadErr;

  const { data: signed, error: signErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 365);
  if (signErr) throw signErr;

  return signed.signedUrl;
}

// =====================================================================
// Asset upload (drag-drop + cross-app import)
// =====================================================================

/**
 * Pick a sensible asset kind from the file's MIME type. Drag-drop callers
 * can override (e.g. to mark a PNG as 'floor_plan').
 */
export function inferAssetKind(file: File): AssetKind {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'photo';
}

/** Cap browser media probes so one bad clip cannot stall a multi-file upload queue. */
const MEDIA_PROBE_TIMEOUT_MS = 8_000;

function withProbeTimeout<T>(
  work: (settle: (value: T) => void) => () => void,
  fallback: T,
  timeoutMs = MEDIA_PROBE_TIMEOUT_MS,
): Promise<T> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: T) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup?.();
      resolve(value);
    };
    const timer = setTimeout(() => settle(fallback), timeoutMs);
    let cleanup: (() => void) | undefined;
    try {
      cleanup = work(settle);
    } catch {
      settle(fallback);
    }
  });
}

async function readAudioDuration(file: File): Promise<number | null> {
  return withProbeTimeout<number | null>((settle) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      settle(Number.isFinite(audio.duration) ? audio.duration : null);
    };
    audio.onerror = () => settle(null);
    audio.src = url;
    return () => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(url);
    };
  }, null);
}

async function readVideoDuration(file: File): Promise<number | null> {
  return withProbeTimeout<number | null>((settle) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      settle(Number.isFinite(v.duration) ? v.duration : null);
    };
    v.onerror = () => settle(null);
    v.src = url;
    return () => {
      v.onloadedmetadata = null;
      v.onerror = null;
      v.removeAttribute('src');
      v.load();
      URL.revokeObjectURL(url);
    };
  }, null);
}

/** Real length of an uploaded video, read by the browser from its URL. */
export async function probeVideoUrlDuration(url: string): Promise<number | null> {
  return withProbeTimeout<number | null>((settle) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.onloadedmetadata = () => {
      settle(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    };
    v.onerror = () => settle(null);
    v.src = url;
    return () => {
      v.onloadedmetadata = null;
      v.onerror = null;
      v.removeAttribute('src');
      v.load();
    };
  }, null);
}

export async function saveAssetDuration(assetId: string, seconds: number): Promise<void> {
  const { error } = await supabase
    .from('propertycut_assets')
    .update({ duration_seconds: Math.round(seconds * 100) / 100 })
    .eq('id', assetId);
  if (error) throw error;
}

async function readImageDimensions(blob: Blob): Promise<{ width: number; height: number } | null> {
  return withProbeTimeout<{ width: number; height: number } | null>((settle) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      settle({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => settle(null);
    img.src = url;
    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
      URL.revokeObjectURL(url);
    };
  }, null);
}

async function readVideoMetaFromBlob(blob: Blob): Promise<number | null> {
  return withProbeTimeout<number | null>((settle) => {
    const url = URL.createObjectURL(blob);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      settle(Number.isFinite(v.duration) ? v.duration : null);
    };
    v.onerror = () => settle(null);
    v.src = url;
    return () => {
      v.onloadedmetadata = null;
      v.onerror = null;
      v.removeAttribute('src');
      v.load();
      URL.revokeObjectURL(url);
    };
  }, null);
}

async function nextOrderIndex(projectId: string): Promise<number> {
  const { data } = await supabase
    .from('propertycut_assets')
    .select('order_index')
    .eq('project_id', projectId)
    .order('order_index', { ascending: false })
    .limit(1);
  const top = (data?.[0] as { order_index?: number } | undefined)?.order_index;
  return typeof top === 'number' ? top + 1 : 0;
}

function uniqueExt(filename: string | null, fallback: string): string {
  if (!filename) return fallback;
  const m = filename.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : fallback;
}

function randomId(): string {
  // Browser-safe; not used as a security token.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

interface UploadAssetArgs {
  userId: string;
  projectId: string;
  file: File;
  /** Override the inferred kind (e.g. mark a PNG upload as 'floor_plan'). */
  kind?: AssetKind;
  /** Byte progress for this file, 0–1. */
  onProgress?: (fraction: number) => void;
}

function encodeStoragePath(path: string): string {
  return path.split('/').map((part) => encodeURIComponent(part)).join('/');
}

/** Storage upload with real byte progress. The JS client does not report it. */
function uploadFileWithProgress(
  storagePath: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const base = process.env.VITE_SUPABASE_URL?.replace(/\/$/, '');
  const anon = process.env.VITE_SUPABASE_ANON_KEY;
  if (!base || !anon) return Promise.reject(new Error('Supabase is not configured.'));

  return supabase.auth.getSession().then(({ data }) => {
    const token = data.session?.access_token;
    if (!token) throw new Error('You need to be signed in to upload.');
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${base}/storage/v1/object/${STORAGE_BUCKET}/${encodeStoragePath(storagePath)}`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('apikey', anon);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.setRequestHeader('cache-control', '3600');
      xhr.setRequestHeader('x-upsert', 'false');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(1);
          resolve();
          return;
        }
        let message = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.message || body?.error) message = body.message || body.error;
        } catch { /* keep status text */ }
        reject(new Error(message));
      };
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.send(file);
    });
  });
}

/**
 * Upload a user-supplied file into the propertycut bucket and create a
 * `propertycut_assets` row at the next free `order_index`.
 */
export async function uploadAsset(args: UploadAssetArgs): Promise<PropertyCutAsset> {
  const kind = args.kind ?? inferAssetKind(args.file);
  const ext = uniqueExt(
    args.file.name,
    kind === 'video' ? 'mp4' : kind === 'audio' ? 'mp3' : 'jpg',
  );
  const storagePath = `assets/${args.userId}/${args.projectId}/${randomId()}.${ext}`;

  await uploadFileWithProgress(storagePath, args.file, args.onProgress);

  const { data: signed, error: signErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
  if (signErr) throw signErr;

  const orderIndex = await nextOrderIndex(args.projectId);

  let duration: number | null = null;
  let width: number | null = null;
  let height: number | null = null;
  if (kind === 'video') {
    duration = await readVideoDuration(args.file);
  } else if (kind === 'audio') {
    duration = await readAudioDuration(args.file);
  } else {
    const dims = await readImageDimensions(args.file);
    if (dims) { width = dims.width; height = dims.height; }
  }

  const { data, error } = await supabase
    .from('propertycut_assets')
    .insert({
      project_id: args.projectId,
      kind,
      storage_path: storagePath,
      signed_url: signed?.signedUrl ?? null,
      order_index: orderIndex,
      duration_seconds: duration,
      trim_start_seconds: 0,
      clip_duration_seconds: null,
      width,
      height,
      file_size_bytes: args.file.size,
      original_filename: args.file.name || null,
    })
    .select('*')
    .single();

  if (error) throw error;
  return data as PropertyCutAsset;
}

// =====================================================================
// Cross-app import (ImageMotion + Hyper Dusk → PropertyCuts)
// =====================================================================

/**
 * Refresh a signed URL on the imagemotion bucket. Mirrors
 * `apps/imagemotion/services/supabase.ts::getFreshVideoUrl` so we don't
 * trip over a stale URL when copying bytes across.
 */
async function getFreshImageMotionUrl(
  storagePath: string | null,
  fallbackUrl: string | null,
): Promise<string | null> {
  if (storagePath) {
    const { data } = await supabase.storage
      .from(IMAGEMOTION_BUCKET)
      .createSignedUrl(storagePath, 60 * 60); // 1 hour is plenty for a one-shot copy
    if (data?.signedUrl) return data.signedUrl;
  }
  return fallbackUrl;
}

/**
 * List the user's completed motion projects from the shared imagemotion
 * tables, filtered to the requested service. Used by ImportPicker.
 *
 * `service: 'imagemotion'` returns standard motion projects (anything not
 * tagged as Hyper Dusk). `service: 'hyperdusk'` returns the rows where
 * `service_type = 'hyper_dusk'`. RLS scopes the query to the calling
 * user, so no `user_id` filter is strictly required, but we add one
 * defensively to keep the query plan tight.
 */
export async function fetchCompletedMotionProjects(args: {
  userId: string;
  service: MotionSource;
}): Promise<MotionProjectSummary[]> {
  const baseSelect =
    'id, name, service_type, status, final_video_url, final_video_path, created_at, ' +
    'imagemotion_project_images(image_url, image_path, dusk_image_url, dusk_image_path, clip_url, clip_path, order_index)';

  let query = supabase
    .from('imagemotion_projects')
    .select(baseSelect)
    .eq('user_id', args.userId)
    .neq('status', 'failed')
    .order('created_at', { ascending: false });

  if (args.service === 'hyperdusk') {
    query = query.eq('service_type', 'hyper_dusk');
  } else {
    // service_type is NOT NULL DEFAULT 'standard' — exclude Hyper Dusk only.
    query = query.neq('service_type', 'hyper_dusk');
  }

  const { data, error } = await query;
  if (error) {
    console.warn('[propertycut] fetchCompletedMotionProjects failed:', error.message);
    throw new Error(error.message);
  }

  const rows = (data ?? []) as any[];
  const out: MotionProjectSummary[] = [];

  for (const p of rows) {
    const images = Array.isArray(p.imagemotion_project_images) ? [...p.imagemotion_project_images] : [];
    images.sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0));
    const first = images[0];
    const hasFinalVideo = Boolean(p.final_video_url || p.final_video_path);
    const hasClips = images.some((img: any) => img.clip_url || img.clip_path);
    const hasDuskImages = images.some((img: any) => img.dusk_image_url || img.dusk_image_path);

    // Skip drafts that have nothing PropertyCuts can actually import.
    if (args.service === 'hyperdusk') {
      if (!hasFinalVideo && !hasDuskImages) continue;
    } else if (!hasFinalVideo && !hasClips) {
      continue;
    }

    // Prefer dusk image as thumbnail for Hyper Dusk; fall back to source image.
    let thumbPath: string | null = null;
    let thumbFallback: string | null = null;
    if (args.service === 'hyperdusk' && first) {
      thumbPath = first.dusk_image_path ?? first.image_path ?? null;
      thumbFallback = first.dusk_image_url ?? first.image_url ?? null;
    } else if (first) {
      thumbPath = first.image_path ?? null;
      thumbFallback = first.image_url ?? null;
    }

    const thumbnailUrl = await getFreshImageMotionUrl(thumbPath, thumbFallback);

    out.push({
      id: p.id as string,
      name: (p.name as string) ?? null,
      service_type: (p.service_type as 'standard' | 'hyper_dusk' | null) ?? null,
      status: p.status as string,
      thumbnail_url: thumbnailUrl,
      final_video_url: (p.final_video_url as string) ?? null,
      final_video_path: (p.final_video_path as string) ?? null,
      created_at: p.created_at as string,
      has_final_video: hasFinalVideo,
      has_clips: hasClips,
      has_dusk_images: hasDuskImages,
    });
  }

  return out;
}

/**
 * List the per-image dusk outputs in a Hyper Dusk project. Powers the
 * "Dusk images" tab inside the importer.
 */
export async function fetchHyperDuskImages(projectId: string): Promise<HyperDuskImageRow[]> {
  const { data, error } = await supabase
    .from('imagemotion_project_images')
    .select('id, project_id, order_index, dusk_image_url, dusk_image_path, image_url, image_path')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (error) {
    console.warn('[propertycut] fetchHyperDuskImages failed:', error.message);
    throw new Error(error.message);
  }

  const rows = (data ?? []) as HyperDuskImageRow[];
  // Refresh signed URLs so the picker thumbnails actually load.
  for (const row of rows) {
    if (row.dusk_image_path || row.dusk_image_url) {
      row.dusk_image_url = await getFreshImageMotionUrl(row.dusk_image_path, row.dusk_image_url);
    }
    if (row.image_path || row.image_url) {
      row.image_url = await getFreshImageMotionUrl(row.image_path, row.image_url);
    }
  }
  // Only return entries that actually have a dusk image.
  return rows.filter((r) => r.dusk_image_url || r.dusk_image_path);
}

/**
 * Copy bytes from a source URL into propertycut-uploads at the given
 * storage path. Returns the new signed URL and file size.
 *
 * Browser proxies the bytes — fine for v1, see PLAN open question.
 */
async function copyToPropertyCutBucket(args: {
  sourcePath?: string | null;
  sourceUrl: string;
  destPath: string;
  contentType?: string;
}): Promise<{ blob: Blob; signedUrl: string | null }> {
  // Prefer the authenticated Storage API so we don't depend on CORS for a
  // cross-origin fetch of the ImageMotion signed URL (that path is what
  // made the import buttons look broken in the browser).
  let blob: Blob | null = null;
  if (args.sourcePath) {
    const { data, error } = await supabase.storage
      .from(IMAGEMOTION_BUCKET)
      .download(args.sourcePath);
    if (!error && data) blob = data;
  }
  if (!blob) {
    if (!args.sourceUrl) {
      throw new Error('Could not download the source asset from storage.');
    }
    const res = await fetch(args.sourceUrl);
    if (!res.ok) throw new Error(`Failed to fetch source asset: ${res.status} ${res.statusText}`);
    blob = await res.blob();
  }

  const { error: uploadErr } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(args.destPath, blob, {
      cacheControl: '3600',
      upsert: false,
      contentType: args.contentType ?? blob.type ?? undefined,
    });
  if (uploadErr) throw uploadErr;

  const { data: signed } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(args.destPath, 60 * 60 * 24 * 7);

  return { blob, signedUrl: signed?.signedUrl ?? null };
}

/**
 * Pull an ImageMotion (or Hyper Dusk) final-video MP4 across the bucket
 * boundary into propertycut-uploads, then insert a `propertycut_assets`
 * row of kind='video'. Returns the new asset row.
 */
export async function importAssetFromImageMotion(args: {
  userId: string;
  projectId: string;
  imagemotionProjectId: string;
}): Promise<PropertyCutAsset> {
  const { data, error } = await supabase
    .from('imagemotion_projects')
    .select('id, name, status, final_video_url, final_video_path')
    .eq('id', args.imagemotionProjectId)
    .single();

  if (error || !data) {
    throw new Error('Source project not found or not accessible.');
  }
  const proj = data as { id: string; name: string | null; status: string; final_video_url: string | null; final_video_path: string | null };
  if (!proj.final_video_path && !proj.final_video_url) {
    throw new Error('No final video found on that project.');
  }

  const sourceUrl = await getFreshImageMotionUrl(proj.final_video_path, proj.final_video_url);
  if (!sourceUrl && !proj.final_video_path) {
    throw new Error('Could not resolve a download URL for the source video.');
  }

  const ext = uniqueExt(proj.final_video_path, 'mp4');
  const destPath = `assets/${args.userId}/${args.projectId}/${randomId()}.${ext}`;
  const { blob, signedUrl } = await copyToPropertyCutBucket({
    sourcePath: proj.final_video_path,
    sourceUrl: sourceUrl ?? '',
    destPath,
    contentType: 'video/mp4',
  });

  const duration = await readVideoMetaFromBlob(blob);
  const orderIndex = await nextOrderIndex(args.projectId);

  const { data: row, error: insertErr } = await supabase
    .from('propertycut_assets')
    .insert({
      project_id: args.projectId,
      kind: 'video',
      storage_path: destPath,
      signed_url: signedUrl,
      order_index: orderIndex,
      duration_seconds: duration,
      trim_start_seconds: 0,
      clip_duration_seconds: null,
      width: null,
      height: null,
      file_size_bytes: blob.size,
      original_filename: proj.name ? `${proj.name}.${ext}` : `imagemotion-${proj.id}.${ext}`,
    })
    .select('*')
    .single();

  if (insertErr) throw insertErr;
  return row as PropertyCutAsset;
}

/**
 * Pull a single Hyper Dusk dusk-image into propertycut-uploads as a
 * `propertycut_assets` row of kind='photo'.
 */
export async function importDuskImageFromHyperDusk(args: {
  userId: string;
  projectId: string;
  imagemotionProjectId: string;
  imagemotionImageId: string;
}): Promise<PropertyCutAsset> {
  const { data, error } = await supabase
    .from('imagemotion_project_images')
    .select('id, project_id, dusk_image_url, dusk_image_path, image_url, image_path')
    .eq('id', args.imagemotionImageId)
    .single();

  if (error || !data) {
    throw new Error('Dusk image not found or not accessible.');
  }
  const row = data as { dusk_image_url: string | null; dusk_image_path: string | null };
  if (!row.dusk_image_path && !row.dusk_image_url) {
    throw new Error('That image has no dusk variant yet.');
  }

  const sourceUrl = await getFreshImageMotionUrl(row.dusk_image_path, row.dusk_image_url);
  if (!sourceUrl && !row.dusk_image_path) {
    throw new Error('Could not resolve a download URL for the dusk image.');
  }

  const ext = uniqueExt(row.dusk_image_path, 'jpg');
  const destPath = `assets/${args.userId}/${args.projectId}/${randomId()}.${ext}`;
  const { blob, signedUrl } = await copyToPropertyCutBucket({
    sourcePath: row.dusk_image_path,
    sourceUrl: sourceUrl ?? '',
    destPath,
  });

  const dims = await readImageDimensions(blob);
  const orderIndex = await nextOrderIndex(args.projectId);

  const { data: insertRow, error: insertErr } = await supabase
    .from('propertycut_assets')
    .insert({
      project_id: args.projectId,
      kind: 'photo',
      storage_path: destPath,
      signed_url: signedUrl,
      order_index: orderIndex,
      duration_seconds: null,
      trim_start_seconds: 0,
      clip_duration_seconds: null,
      width: dims?.width ?? null,
      height: dims?.height ?? null,
      file_size_bytes: blob.size,
      original_filename: `hyperdusk-${args.imagemotionImageId}.${ext}`,
    })
    .select('*')
    .single();

  if (insertErr) throw insertErr;
  return insertRow as PropertyCutAsset;
}

/**
 * List accepted motion clips in an ImageMotion project.
 */
export async function fetchImageMotionClips(projectId: string): Promise<ImageMotionClipRow[]> {
  const { data, error } = await supabase
    .from('imagemotion_project_images')
    .select('id, project_id, order_index, clip_url, clip_path, image_url, image_path, clip_duration')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (error) {
    console.warn('[propertycut] fetchImageMotionClips failed:', error.message);
    throw new Error(error.message);
  }

  const rows = (data ?? []) as ImageMotionClipRow[];
  for (const row of rows) {
    if (row.clip_path || row.clip_url) {
      row.clip_url = await getFreshImageMotionUrl(row.clip_path, row.clip_url);
    }
    if (row.image_path || row.image_url) {
      row.image_url = await getFreshImageMotionUrl(row.image_path, row.image_url);
    }
  }
  return rows.filter((r) => r.clip_url || r.clip_path);
}

/**
 * Pull a single ImageMotion clip into propertycut-uploads as a video asset.
 */
export async function importClipFromImageMotion(args: {
  userId: string;
  projectId: string;
  imagemotionImageId: string;
}): Promise<PropertyCutAsset> {
  const { data, error } = await supabase
    .from('imagemotion_project_images')
    .select('id, project_id, clip_url, clip_path, clip_duration')
    .eq('id', args.imagemotionImageId)
    .single();

  if (error || !data) {
    throw new Error('Clip not found or not accessible.');
  }
  const row = data as {
    clip_url: string | null;
    clip_path: string | null;
    clip_duration: number | null;
  };
  if (!row.clip_path && !row.clip_url) {
    throw new Error('That image has no motion clip yet.');
  }

  const sourceUrl = await getFreshImageMotionUrl(row.clip_path, row.clip_url);
  if (!sourceUrl && !row.clip_path) {
    throw new Error('Could not resolve a download URL for the clip.');
  }

  const ext = uniqueExt(row.clip_path, 'mp4');
  const destPath = `assets/${args.userId}/${args.projectId}/${randomId()}.${ext}`;
  const { blob, signedUrl } = await copyToPropertyCutBucket({
    sourcePath: row.clip_path,
    sourceUrl: sourceUrl ?? '',
    destPath,
    contentType: 'video/mp4',
  });

  const duration = row.clip_duration ?? await readVideoMetaFromBlob(blob);
  const orderIndex = await nextOrderIndex(args.projectId);

  const { data: insertRow, error: insertErr } = await supabase
    .from('propertycut_assets')
    .insert({
      project_id: args.projectId,
      kind: 'video',
      storage_path: destPath,
      signed_url: signedUrl,
      order_index: orderIndex,
      duration_seconds: duration,
      trim_start_seconds: 0,
      clip_duration_seconds: null,
      width: null,
      height: null,
      file_size_bytes: blob.size,
      original_filename: `imagemotion-clip-${args.imagemotionImageId}.${ext}`,
    })
    .select('*')
    .single();

  if (insertErr) throw insertErr;
  return insertRow as PropertyCutAsset;
}

// =====================================================================
// Project status helpers
// =====================================================================

export async function setProjectStatus(
  projectId: string,
  status: PropertyCutProject['status'],
): Promise<void> {
  const { error } = await supabase
    .from('propertycut_projects')
    .update({ status })
    .eq('id', projectId);
  if (error) throw error;
}

export async function deleteAsset(assetId: string): Promise<void> {
  // Best-effort: also delete the underlying object so we don't leak storage.
  const { data } = await supabase
    .from('propertycut_assets')
    .select('storage_path')
    .eq('id', assetId)
    .single();
  if (data?.storage_path) {
    await supabase.storage.from(STORAGE_BUCKET).remove([data.storage_path as string]);
  }
  const { error } = await supabase
    .from('propertycut_assets')
    .delete()
    .eq('id', assetId);
  if (error) throw error;
}
